import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DateRange, RangeKeyDict } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './DateRangePicker.css';
import { tr } from 'date-fns/locale';
import { format } from 'date-fns';

export interface DateRangePickerProps {
    startDate?: Date | null;
    endDate?: Date | null;
    /** Tarih değiştiğinde çağrılacak fonksiyon
     * @param startDate 
     * @param endDate 
     */
    onChange?: (startDate: Date | null, endDate: Date | null) => void;
    /** Seçili tarihi temizlemek için çağrılacak fonksiyon. Verilirse trigger üzerinde X ikonu gösterilir. */
    onClear?: () => void;
    /** Tarih seçilmediğinde trigger'da gösterilecek placeholder metin. Verilmezse 'Tarih seçiniz' gösterilir. */
    label?: string;
    /** true ise "Bu Yıl / Geçen Yıl / Tüm Zamanlar" presetleri yerine
     *  "Son 1 Gün / 1 Hafta / 1 Ay Dokunulmamış" presetleri gösterilir. */
    notTouched?: boolean;
    minDate?: Date;
    maxDate?: Date;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
    moveRangeOnFirstSelection?: boolean;
    direction?: 'horizontal' | 'vertical';
    dateDisplayFormat?: string;
    weekdayDisplayFormat?: string;
    loading?: boolean;
    useDropdown?: boolean;
}

interface DateRangeState {
    startDate: Date;
    endDate: Date;
    key: string;
}

const startOfDay = (date: Date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const endOfDay = (date: Date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
    startDate = null,
    endDate = null,
    onChange,
    onClear,
    label,
    notTouched = false,
    minDate,
    maxDate,
    disabled = false,
    className,
    style,
    moveRangeOnFirstSelection = false,
    direction = 'horizontal',
    dateDisplayFormat = 'dd.MM.yyyy',
    weekdayDisplayFormat = 'EEE',
    loading = false,
    useDropdown = false,
}) => {
    const months = 2;
    const computedDirection = direction;
    const effectiveMinDate = startOfDay(minDate ?? new Date(2010, 0, 1));
    const effectiveMaxDate = endOfDay(maxDate ?? new Date());

    const initialRange: DateRangeState = {
        startDate: startDate || new Date(),
        endDate: endDate || new Date(),
        key: 'selection',
    };

    const [ranges, setRanges] = useState<DateRangeState[]>([initialRange]);
    const prevValidRangeRef = useRef<DateRangeState>(initialRange);
    const [focusedRange, setFocusedRange] = useState<[number, number]>([0, 0]);
    const [shownDate, setShownDate] = useState<Date>(endDate || startDate || new Date());
    const [calendarKey, setCalendarKey] = useState(0);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownAlign, setDropdownAlign] = useState<'left' | 'right'>('left');
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const awaitingEndSelectionRef = useRef(false);
    const prevLoadingRef = useRef(loading);
    const committedWhileOpenRef = useRef(false);

    const commitRangeToParent = useCallback(
        (start: Date, end: Date) => {
            if (open && useDropdown) {
                committedWhileOpenRef.current = true;
            }
            if (onChange) {
                onChange(start, end);
            }
        },
        [onChange, open, useDropdown],
    );

    const closePopover = useCallback(() => {
        if (awaitingEndSelectionRef.current) {
            const current = prevValidRangeRef.current;
            commitRangeToParent(current.startDate, current.endDate);
            awaitingEndSelectionRef.current = false;
        }
        setOpen(false);
    }, [commitRangeToParent]);

    const interactionDisabled = disabled || (loading && !open);

    useEffect(() => {
        if (prevLoadingRef.current && !loading && open && committedWhileOpenRef.current) {
            committedWhileOpenRef.current = false;
            awaitingEndSelectionRef.current = false;
            setOpen(false);
        }
        prevLoadingRef.current = loading;
    }, [loading, open]);

    useEffect(() => {
        if (!open || !committedWhileOpenRef.current || loading) return;

        const timer = window.setTimeout(() => {
            if (committedWhileOpenRef.current && !loading && open) {
                committedWhileOpenRef.current = false;
                awaitingEndSelectionRef.current = false;
                setOpen(false);
            }
        }, 300);

        return () => window.clearTimeout(timer);
    }, [loading, open]);

    const updateDropdownPosition = useCallback(() => {
        if (!containerRef.current || !dropdownRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const dropdownWidth = dropdownRef.current.offsetWidth;
        const dropdownHeight = dropdownRef.current.offsetHeight;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const gap = 4;

        const alignRight = containerRect.left + dropdownWidth > viewportWidth - 8;
        setDropdownAlign(alignRight ? 'right' : 'left');

        let top = containerRect.bottom + gap;
        if (
            top + dropdownHeight > viewportHeight - 8 &&
            containerRect.top - dropdownHeight - gap >= 0
        ) {
            top = containerRect.top - dropdownHeight - gap;
        }

        const style: React.CSSProperties = {
            position: 'fixed',
            top,
            zIndex: loading ? 1100 : 1300,
        };

        if (alignRight) {
            style.right = viewportWidth - containerRect.right;
        } else {
            style.left = containerRect.left;
        }

        setDropdownStyle(style);
    }, [loading]);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                containerRef.current?.contains(target) ||
                dropdownRef.current?.contains(target)
            ) {
                return;
            }
            closePopover();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, closePopover]);

    useLayoutEffect(() => {
        if (!open) return;
        updateDropdownPosition();
        const rafId = requestAnimationFrame(updateDropdownPosition);
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [open, updateDropdownPosition, calendarKey]);


    useEffect(() => {
        if (disabled || (loading && !open)) return;
        if (useDropdown && !open) return;

        const fieldIndex = focusedRange[1];
        const timer = setTimeout(() => {
            const inputs = wrapperRef.current?.querySelectorAll('.rdrDateInput input');
            if (inputs && inputs.length > fieldIndex) {
                const input = inputs[fieldIndex] as HTMLInputElement;
                if (document.activeElement !== input) {
                    try {
                        input.focus({ preventScroll: true });
                    } catch {
                        input.focus();
                    }
                }
            }
        }, 0);

        return () => clearTimeout(timer);
    }, [focusedRange, disabled, loading, useDropdown, open]);

    // inputlar salt-okunur tutarak manuel tarih girişi engellendi.
    useEffect(() => {
        const inputs = wrapperRef.current?.querySelectorAll<HTMLInputElement>('.rdrDateInput input');
        if (!inputs || inputs.length === 0) return;

        inputs.forEach((input) => {
            input.readOnly = true;
            input.setAttribute('inputmode', 'none');
            input.setAttribute('aria-readonly', 'true');
            input.classList.add('drp-input-readonly');
        });
    }, [ranges, open, useDropdown]);

    const disabledDay = useCallback((date: Date) => {
        const current = ranges[0];
        if (!current) return false;

        const day = startOfDay(date).getTime();
        const startTs = current.startDate ? startOfDay(current.startDate).getTime() : null;
        const endTs = current.endDate ? startOfDay(current.endDate).getTime() : null;

        const focusingEnd = focusedRange?.[1] === 1;
        const focusingStart = focusedRange?.[1] === 0;

        if (focusingEnd && startTs !== null) {
            return day < startTs;
        }

        if (focusingStart && endTs !== null) {
            return day > endTs;
        }

        return false;
    }, [ranges, focusedRange]);


    useEffect(() => {
        if (!startDate && !endDate) {
            const today = new Date();
            const next = { startDate: today, endDate: today, key: 'selection' };
            prevValidRangeRef.current = next;
            setRanges([next]);
            return;
        }

        const newStartDate = startDate || new Date();
        const endDateValue = endDate || new Date();

        const currentRange = ranges[0];
        const needsUpdate =
            !currentRange ||
            currentRange.startDate.getTime() !== newStartDate.getTime() ||
            currentRange.endDate.getTime() !== endDateValue.getTime();

        if (needsUpdate) {
            const next = {
                startDate: newStartDate,
                endDate: endDateValue,
                key: 'selection',
            };
            prevValidRangeRef.current = next;
            setRanges([next]);
            awaitingEndSelectionRef.current = false;
        }
    }, [startDate, endDate]);

    const handleRangeChange = useCallback(
        (item: RangeKeyDict) => {
            const selection = item.selection;
            if (!selection?.startDate || !selection?.endDate) {
                setRanges([prevValidRangeRef.current]);
                return;
            }

            const range = validateAndNormalize(
                startOfDay(selection.startDate),
                endOfDay(selection.endDate),
                effectiveMinDate,
                effectiveMaxDate
            );

            prevValidRangeRef.current = {
                startDate: range.startDate,
                endDate: range.endDate,
                key: 'selection',
            };

            setRanges([{
                startDate: range.startDate,
                endDate: range.endDate,
                key: 'selection',
            }]);

            if (!moveRangeOnFirstSelection) {
                if (!awaitingEndSelectionRef.current) {
                    awaitingEndSelectionRef.current = true;
                    return;
                }
            }

            awaitingEndSelectionRef.current = false;
            commitRangeToParent(range.startDate, range.endDate);
        },
        [commitRangeToParent, effectiveMinDate, effectiveMaxDate, moveRangeOnFirstSelection]
    );

    // preset hesaplamaları
    const clampDate = (date: Date, min?: Date, max?: Date) => {
        if (!date) return date;
        let result = date;
        if (min && date < min) result = min;
        if (max && date > max) result = max;
        return result;
    };

    const validateAndNormalize = (start: Date, end: Date, min?: Date, max?: Date) => {
        const clampedStart = clampDate(start, min, max);
        const clampedEnd = clampDate(end, min, max);

        return {
            startDate: clampedStart,
            endDate: clampedEnd,
        };
    };

    const getStartOfMonth = (base: Date) => {
        return startOfDay(new Date(base.getFullYear(), base.getMonth(), 1));
    };

    const getEndOfMonth = (base: Date) => {
        return endOfDay(new Date(base.getFullYear(), base.getMonth() + 1, 0));
    };

    const presets = useMemo(() => {
        const now = new Date();
        const todayStart = startOfDay(now);
        const todayEnd = endOfDay(now);

        const yesterdayStart = startOfDay(new Date(now.getTime() - 24 * 60 * 60 * 1000));
        const yesterdayEnd = endOfDay(new Date(now.getTime() - 24 * 60 * 60 * 1000));

        const last7Start = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6));
        const last30Start = startOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29));

        const thisMonthStart = getStartOfMonth(now);
        const thisMonthEnd = getEndOfMonth(now);

        const lastMonthBase = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthStart = getStartOfMonth(lastMonthBase);
        const lastMonthEnd = getEndOfMonth(lastMonthBase);

        const thisYearStart = startOfDay(new Date(now.getFullYear(), 0, 1));
        const thisYearEnd = endOfDay(new Date(now.getFullYear(), 11, 31));

        const lastYearStart = startOfDay(new Date(now.getFullYear() - 1, 0, 1));
        const lastYearEnd = endOfDay(new Date(now.getFullYear() - 1, 11, 31));

        const allTimeStart = startOfDay(new Date(2010, 0, 1));
        const allTimeEnd = todayEnd;

        const notTouched1DayEnd = endOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
        const notTouched7DayEnd = endOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7));
        const notTouched30DayEnd = endOfDay(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30));

        const lastThreePresets = notTouched
            ? [
                { label: 'Son 1 Gün Hariç', startDate: allTimeStart, endDate: notTouched1DayEnd },
                { label: 'Son 1 Hafta Hariç', startDate: allTimeStart, endDate: notTouched7DayEnd },
                { label: 'Son 1 Ay Hariç', startDate: allTimeStart, endDate: notTouched30DayEnd },
            ]
            : [
                { label: 'Bu Yıl', startDate: thisYearStart, endDate: thisYearEnd },
                { label: 'Geçen Yıl', startDate: lastYearStart, endDate: lastYearEnd },
                { label: 'Tüm Zamanlar', startDate: allTimeStart, endDate: allTimeEnd },
            ];

        return [
            { label: 'Bugün', startDate: todayStart, endDate: todayEnd },
            { label: 'Dün', startDate: yesterdayStart, endDate: yesterdayEnd },
            { label: 'Son 7 Gün', startDate: last7Start, endDate: todayEnd },
            { label: 'Son 30 Gün', startDate: last30Start, endDate: todayEnd },
            { label: 'Bu Ay', startDate: thisMonthStart, endDate: thisMonthEnd },
            { label: 'Geçen Ay', startDate: lastMonthStart, endDate: lastMonthEnd },
            ...lastThreePresets,
        ];
    }, [notTouched]);

    const applyRange = (start: Date, end: Date) => {
        const nextStart = startOfDay(start);
        const nextEnd = endOfDay(end);
        const range = validateAndNormalize(nextStart, nextEnd, effectiveMinDate, effectiveMaxDate);

        prevValidRangeRef.current = {
            startDate: range.startDate,
            endDate: range.endDate,
            key: 'selection',
        };
        setRanges([{
            startDate: range.startDate,
            endDate: range.endDate,
            key: 'selection',
        }]);
        setShownDate(range.endDate);
        setCalendarKey((k) => k + 1);
        awaitingEndSelectionRef.current = false;
        commitRangeToParent(range.startDate, range.endDate);
    };

    const isPresetActive = (presetStart: Date, presetEnd: Date) => {
        const current = ranges[0];
        if (!current) return false;
        return current.startDate.getTime() === presetStart.getTime()
            && current.endDate.getTime() === presetEnd.getTime();
    };

    const formatDisplay = (date: Date | null) => {
        if (!date || isNaN(date.getTime())) return '';
        try {
            return format(date, dateDisplayFormat, { locale: tr });
        } catch {
            return new Intl.DateTimeFormat('tr-TR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }).format(date);
        }
    };

    const rangeLabel = useMemo(() => {
        if (!startDate && !endDate) return '';
        const current = ranges[0];
        if (!current) return '';
        return `${formatDisplay(current.startDate)} - ${formatDisplay(current.endDate)}`;
    }, [startDate, endDate, ranges, dateDisplayFormat]);

    const toggleOpen = () => {
        if (disabled) return;
        if (loading && !open) return;
        setOpen((prev) => {
            if (!prev) {
                awaitingEndSelectionRef.current = false;
                committedWhileOpenRef.current = false;
            }
            return !prev;
        });
    };

    const calendarContent = (
        <div
            className={`drp-wrapper ${className || ''}`}
            style={style}
        >
            <div className="drp-panel">
                <div className="drp-presets">
                    {presets.map((preset) => {
                        const active = isPresetActive(preset.startDate, preset.endDate);
                        return (
                            <button
                                key={preset.label}
                                type="button"
                                className={`drp-preset-btn ${active ? 'active' : ''}`}
                                onClick={() => !interactionDisabled && applyRange(preset.startDate, preset.endDate)}
                                disabled={interactionDisabled}
                                aria-pressed={active}
                            >
                                {preset.label}
                            </button>
                        );
                    })}
                </div>

                <div className="drp-calendar">
                    <DateRange
                        // react-date-range component'inin props'ları
                        key={calendarKey}
                        ranges={ranges}
                        onChange={handleRangeChange}
                        moveRangeOnFirstSelection={moveRangeOnFirstSelection}
                        editableDateInputs={false}
                        direction={computedDirection}
                        months={months}
                        minDate={effectiveMinDate}
                        maxDate={effectiveMaxDate}
                        disabled={interactionDisabled}
                        locale={tr}
                        dateDisplayFormat={dateDisplayFormat}
                        weekdayDisplayFormat={weekdayDisplayFormat}
                        weekStartsOn={1}
                        showMonthAndYearPickers
                        focusedRange={focusedRange}
                        onRangeFocusChange={(fr) => setFocusedRange(fr)}
                        startDatePlaceholder="Başlangıç Tarihi"
                        endDatePlaceholder="Bitiş Tarihi"
                        disabledDay={disabledDay}
                        shownDate={shownDate}
                    />
                </div>
            </div>
        </div>
    );

    if (useDropdown) {
        return (
            <div ref={containerRef} className="drp-dropdown-container">
                <button
                    type="button"
                    className={`drp-trigger${open ? ' drp-trigger-open' : ''}`}
                    onClick={toggleOpen}
                    disabled={interactionDisabled}
                >
                    <span className="drp-trigger-label">
                        {rangeLabel || label || 'Tarih seçiniz'}
                    </span>
                    <span className="drp-trigger-icons">
                        {onClear && (startDate || endDate) && (
                            <span
                                className="drp-clear-btn"
                                role="button"
                                aria-label="Tarihi temizle"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    awaitingEndSelectionRef.current = false;
                                    onClear();
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </span>
                        )}
                        <span className="drp-trigger-icon" aria-hidden="true">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M4.995 5H19.005C20.107 5 21 5.895 21 6.994V19.006C21 19.2679 20.9484 19.5273 20.8481 19.7693C20.7478 20.0113 20.6009 20.2312 20.4156 20.4163C20.2304 20.6015 20.0104 20.7484 19.7684 20.8485C19.5263 20.9487 19.2669 21.0001 19.005 21H4.995C4.46607 21 3.95878 20.7899 3.58468 20.416C3.21057 20.0421 3.00027 19.5349 3 19.006V6.994C3 5.893 3.892 5 4.995 5ZM5 9V18C5 18.2652 5.10536 18.5196 5.29289 18.7071C5.48043 18.8946 5.73478 19 6 19H18C18.2652 19 18.5196 18.8946 18.7071 18.7071C18.8946 18.5196 19 18.2652 19 18V9H5ZM6 4C6 3.73478 6.10536 3.48043 6.29289 3.29289C6.48043 3.10536 6.73478 3 7 3C7.26522 3 7.51957 3.10536 7.70711 3.29289C7.89464 3.48043 8 3.73478 8 4V5H6V4ZM16 4C16 3.73478 16.1054 3.48043 16.2929 3.29289C16.4804 3.10536 16.7348 3 17 3C17.2652 3 17.5196 3.10536 17.7071 3.29289C17.8946 3.48043 18 3.73478 18 4V5H16V4ZM7 13V10.999H9V13H7ZM15 13V10.999H17V13H15ZM11 13V10.999H13.001V13H11ZM7 17V15H9V17H7ZM11 17V15H13.001V17H11ZM15 17V15H17V17H15Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </span>
                    </span>
                </button>
                {open &&
                    createPortal(
                        <div
                            ref={dropdownRef}
                            className={`drp-dropdown-menu drp-dropdown-menu--portal drp-dropdown-menu--${dropdownAlign}${loading ? ' drp-dropdown-menu--behind-backdrop' : ''}`}
                            style={dropdownStyle}
                        >
                            {calendarContent}
                        </div>,
                        document.body,
                    )}
            </div>
        );
    }

    return (
        <>
            {calendarContent}
        </>
    );
};
DateRangePicker.displayName = 'DateRangePicker';

